import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { AsyncSubject, Subject } from 'rxjs';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { Editor, EditorOptions } from 'tinymce';
import { curlyQuotesCmn } from './custom-commands/curly-quotes';
import { CustomCommands } from './enums/custom-commands';
import { EditorToolbar } from './types/editor-toolbar/editor-toolbar';
import { BODY_CONTROL } from 'src/app/shared/injectors/control-injector';
import { ILinkInsert } from 'src/app/core/dtos/interfaces/i-link-insert';
import { DataLinkType } from 'src/app/core/dtos/enums/data-link-type';
import { DataBlockType } from 'src/app/core/dtos/enums/data-block-type';
import { BodyLinkFormat } from 'src/app/core/dtos/enums/body-link-format';
import { toSafeHref } from 'src/app/shared/utils/safe-href';
import {
  IPrivateMarketsFundBodyLinkRequest,
  PrivateMarketsBodyLinkResult,
} from 'src/app/core/dtos/interfaces/i-private-markets-body-link';
import {
  ClauseMissReason,
  explainClauseMiss,
  findClauseBlocks,
  findMentionRange,
} from './body-mention-locator';

// NOTE: A body block reaches the website as a bare `<div data-block-type="…"></div>`; the two
// attributes below are editor-only chrome, added on parse and stripped on serialize (ADR-0003).
const BLOCK_TYPE_ATTRIBUTE = 'data-block-type';
const BLOCK_NOTE_ATTRIBUTE = 'data-cms-block-note';
const BLOCK_REMOVE_ATTRIBUTE = 'data-cms-block-remove';

// NOTE: PM-143 card format preview. It lands on an element the CMS does not own — the author's
// paragraph, or their anchor — so it is stripped via addTempAttr, not a filter (ADR-0004).
const CARD_NOTE_ATTRIBUTE = 'data-cms-card-note';

// NOTE: PM-144 mention flash. Stripped via addTempAttr for the card note's reason above — it
// lands on the author's own paragraph (ADR-0007).
const MENTION_FLASH_ATTRIBUTE = 'data-cms-mention-flash';

// NOTE: This selector is the only gate — no @Input and no feature flag, deliberately (ADR-0004).
const CARD_LINK_SELECTOR =
  `a[data-link-type="${DataLinkType.PRIVATE_MARKETS_FUND}"]` +
  `[data-format="${BodyLinkFormat.Card}"]`;

const BLOCK_LABELS: Record<string, string> = {
  [DataBlockType.PRIVATE_MARKETS_FUNDS]: 'Funds in this story',
};

const RECOGNISED_BLOCK_TYPES: ReadonlySet<string> = new Set(
  Object.values(DataBlockType)
);

export type InsertBlockResult = 'inserted' | 'existing' | 'unavailable';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;',
};

// Every _get*Markup builder below hands its result to editor.insertContent(), which parses it as
// real HTML — an innerHTML sink. Values interpolated into that markup (names, ids sourced from
// smart-linking or media DTOs) must be neutralised first, in both text and attribute position.
function escapeHtml(value: string | null | undefined): string {
  return (value ?? '').replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

@Component({
  selector: 'app-content-editor',
  templateUrl: './content-editor.component.html',
  styleUrls: ['./content-editor.component.scss'],
})
export class ContentEditorComponent implements OnInit {
  // TODO IMPROVEMENT: Should we have a global language app service feature?
  @Input() languageCode = 'en';
  @Input() sectionFormControl: FormControl;
  @Input() sectionImgsFormControl: FormArray;
  @Input() sectionVideosFormControl: FormArray;
  @Input() isSlide = false;
  @Input() isDisabled = false;
  @Input() title = 'Content Body';
  @Input() customHeight: number | string = 600;
  @Input() customToolbar: EditorToolbar =
    'undo redo styles style-h2 style-h3 bold italic blockquote boxout bullist numlist code link charmap fullscreen';
  @Output() handleAfterInit = new EventEmitter<void>();
  @Output() handleUpdatedBody = new EventEmitter<string>();

  editor: Editor;
  settings: Partial<EditorOptions>;

  editorSubject: Subject<any> = new AsyncSubject();

  private EMPTY_THUMBNAIL_URL = `./assets/emptyThumbnail.img`;

  private readonly EDITOR_PLUGINS = [
    'advlist',
    'autolink',
    'lists',
    'link',
    'image',
    'charmap',
    'preview',
    'anchor',
    'searchreplace',
    'visualblocks',
    'code',
    'fullscreen',
    'insertdatetime',
    'media',
    'table',
    'code',
    'help',
    'wordcount',
  ];

  private readonly CUSTOM_STYLE_FORMATS = [
    {
      title: 'Custom Styles',
      items: [
        {
          title: 'Highlight Blue - Block',
          inline: 'span',
          classes: 'body__highlight body__highlight--blue',
        },
        {
          title: 'Highlight Red',
          inline: 'span',
          classes: 'body__highlight body__highlight--red',
        },
        {
          title: 'Highlight Green',
          inline: 'span',
          classes: 'body__highlight body__highlight--green',
        },
        { title: 'Footnote', inline: 'span', classes: 'body__footnote' },
        // {title: 'Button CTA', selector : 'a', classes : 'body__button'}
      ],
    },
    {
      title: 'Headers',
      items: [
        { title: 'Header 2', format: 'h2' },
        { title: 'Header 3', format: 'h3' },
        { title: 'Header 4', format: 'h4' },
        { title: 'Header 5', format: 'h5' },
        { title: 'Header 6', format: 'h6' },
      ],
    },
    {
      title: 'Inline',
      items: [
        { title: 'Bold', icon: 'bold', format: 'bold' },
        { title: 'Italic', icon: 'italic', format: 'italic' },
        { title: 'Underline', icon: 'underline', format: 'underline' },
        {
          title: 'Strikethrough',
          icon: 'strike-through',
          format: 'strikethrough',
        },
        {
          title: 'Superscript',
          icon: 'superscript',
          format: 'superscript',
        },
        { title: 'Subscript', icon: 'subscript', format: 'subscript' },
        { title: 'Code', icon: 'sourcecode', format: 'code' },
      ],
    },
    {
      title: 'Blocks',
      items: [
        { title: 'Paragraph', format: 'p' },
        { title: 'Blockquote', format: 'blockquote' },
        { title: 'Div', format: 'div' },
        { title: 'Pre', format: 'pre' },
      ],
    },
    {
      title: 'Alignment',
      items: [
        { title: 'Left', icon: 'align-left', format: 'alignleft' },
        { title: 'Center', icon: 'align-center', format: 'aligncenter' },
        { title: 'Right', icon: 'align-right', format: 'alignright' },
        { title: 'Justify', icon: 'align-justify', format: 'alignjustify' },
      ],
    },
  ];

  constructor(@Inject(BODY_CONTROL) bodyControlInjected: FormControl) {
    this.sectionFormControl = this.sectionFormControl || bodyControlInjected;
  }

  ngOnInit(): void {
    this.setupEditor();
  }

  setupEditor(): void {
    this.settings = {
      branding: false,
      promotion: false,
      content_css: ['/cms/tinyStyles.css'],
      body_class: 'cms-tinymce-editor',
      custom_elements: 'dynamic-poll,link',
      style_formats: this.CUSTOM_STYLE_FORMATS,
      menubar: false,
      height: this.customHeight,
      toolbar: this.customToolbar,
      toolbar_mode: 'wrap',
      paste_as_text: true,
      language: this.languageCode,
      plugins: this.EDITOR_PLUGINS,
      // TODO DEV-NOTE: Allow script tags, so that we can add javascript in the source code editor.
      extended_valid_elements:
        'script[charset|defer|language|src|type],dynamic-poll[poll-id|multiple],link[rel|href]',
      // TODO DEV-NOTE: Allow style tags, so that we can add style in the source code editor.
      valid_children: '+body[style], +style[type]',
      browser_spellcheck: true,
      contextmenu: ['spellchecker'],
      setup: (editor: Editor) => {
        this.editor = editor;
        this._addCustomButtons(editor);
        this._addCurlyQuotes(editor);
        this._addBodyBlockChromeFilters(editor);
        this._keepBodyBlockNotesLive(editor);
        this._addBodyBlockRemoveAction(editor);
        this._addCardNoteTempAttr(editor);
        this._keepCardNotesLive(editor);
        this._addMentionFlashTempAttr(editor);

        const formatFootnoteContent = () => {
          const paragraphs = editor.getBody().querySelectorAll('p');
          paragraphs.forEach((p) => {
            if (p.querySelector('span.body__footnote')) {
              p.classList.add('footnote__border');
            } else {
              p.classList.remove('footnote__border');
            }
          });
        };

        editor.on('init', (e) => {
          formatFootnoteContent();
          const savedScrollPosition = localStorage.getItem('tinymceScrollPos');
          if (savedScrollPosition) {
            const scrollPos = parseInt(savedScrollPosition, 10);
            editor.getWin().scrollTo(0, scrollPos);
            const scrollTop = editor.getWin().scrollY;
            localStorage.setItem('tinymceScrollPos', scrollTop.toString());
          }

          editor.formatter.register('customBlockQuote', {
            block: 'blockquote',
            classes: 'boxout',
            exact: true,
            wrapper: true,
          });
        });

        editor.on('ScrollContent', () => {
          const scrollTop = editor.getWin().scrollY;
          localStorage.setItem('tinymceScrollPos', scrollTop.toString());
        });

        editor.on('keyUp', (e) => {
          editor.execCommand(CustomCommands.CURLY_QUOTES);

          formatFootnoteContent();
        });

        editor.on('FormatApply', (e) => {
          formatFootnoteContent();
        });

        editor.on('FormatRemove', (e) => {
          formatFootnoteContent();
        });

        const regex =
          /<span[^>]*style="[^"]*background-color:\s*rgb\(255\s+255\s+255\s*\/\s*var\(--tw-bg-opacity\)\);[^"]*font-size:\s*(1rem|0\.875rem);[^"]*"[^>]*>/g;

        const cleanEditorContent = (editor: Editor, regex: RegExp) => {
          if (!regex.test(editor.getBody().innerHTML)) {
            return;
          }
          const bookmark = editor.selection.getBookmark(2, true);
          editor.getBody().innerHTML = editor
            .getBody()
            .innerHTML.replace(regex, '')
            .replace(/<\/span>/g, '');
          editor.selection.moveToBookmark(bookmark);
        };

        editor.on('dragend', function (e) {
          cleanEditorContent(editor, regex);
        });

        editor.on('input', function (e) {
          cleanEditorContent(editor, regex);
        });

        window.addEventListener('beforeunload', () => {
          localStorage.removeItem('tinymceScrollPos');
        });
      },
    };
  }

  onEditorInit(event: any): void {
    this._removeCDATAOnScripts();
    this.editorSubject.next(event.editor);
    this.editorSubject.complete();
    this.handleAfterInit.emit();
  }

  private _addCustomButtons(editor: Editor): void {
    ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach((name) => {
      editor.ui.registry.addButton(`style-${name}`, {
        tooltip: `Toggle ${name}`,
        text: name.toUpperCase(),
        onAction: () => editor.execCommand('mceToggleFormat', false, name),
      });
    });

    editor.ui.registry.addToggleButton('boxout', {
      icon: 'select-all',
      tooltip: 'Boxout',
      onAction: () => {
        const node = editor.selection.getNode();
        const blockquoteNode = editor.dom.getParent(node, 'blockquote');
        const isBoxout =
          blockquoteNode && blockquoteNode.classList.contains('boxout');

        if (blockquoteNode && !isBoxout) {
          editor.execCommand('mceBlockQuote', false);
          editor.formatter.toggle('customBlockQuote');
        } else if (blockquoteNode && isBoxout) {
          editor.formatter.toggle('customBlockQuote');
        } else {
          editor.formatter.toggle('customBlockQuote');
        }
      },
      onSetup: (buttonApi) => {
        const nodeChangeHandler = () => {
          const node = editor.selection.getNode();
          const blockquoteNode = editor.dom.getParent(node, 'blockquote');
          const isBoxout =
            blockquoteNode && blockquoteNode.classList.contains('boxout');

          if (blockquoteNode && isBoxout) {
            buttonApi.setActive(true);
          } else {
            buttonApi.setActive(false);
          }
        };

        editor.on('NodeChange', nodeChangeHandler);

        return () => editor.off('NodeChange', nodeChangeHandler);
      },
    });
  }

  private _addCurlyQuotes(editor: Editor): void {
    this.editor.addCommand(CustomCommands.CURLY_QUOTES, () =>
      curlyQuotesCmn(editor)
    );
  }

  /* istanbul ignore next */
  private _removeCDATAOnScripts(): void {
    // TODO DEV-NOTE: http://stackoverflow.com/a/35517732
    this.editor.serializer.addNodeFilter('script,style', (nodes, _) => {
      let i = nodes.length;
      let node;
      let value;

      function trim(incommingValue: string): string {
        return incommingValue
          .replace(/(<!--\[CDATA\[|\]\]-->)/g, '\n')
          .replace(/^[\r\n]*|[\r\n]*$/g, '')
          .replace(
            /^\s*((<!--)?(\s*\/\/)?\s*<!\[CDATA\[|(<!--\s*)?\/\*\s*<!\[CDATA\[\s*\*\/|(\/\/)?\s*<!--|\/\*\s*<!--\s*\*\/)\s*[\r\n]*/gi,
            ''
          )
          .replace(
            /\s*(\/\*\s*\]\]>\s*\*\/(-->)?|\s*\/\/\s*\]\]>(-->)?|\/\/\s*(-->)?|\]\]>|\/\*\s*-->\s*\*\/|\s*-->\s*)\s*$/g,
            ''
          );
      }

      while (i--) {
        node = nodes[i];
        value = node.firstChild ? node.firstChild.value : '';

        if (value.length > 0) {
          node.firstChild.value = trim(value);
        }
      }
    });
  }

  // NOTE: PreInit, not onEditorInit(): the Angular wrapper writes the form control's value into
  // the editor from its own 'init' handler, bound before ours, so a filter registered later would
  // miss the body the editor loads with and existing blocks would come up bare.
  private _addBodyBlockChromeFilters(editor: Editor): void {
    editor.on('PreInit', () => {
      editor.parser.addAttributeFilter(BLOCK_TYPE_ATTRIBUTE, (nodes) => {
        nodes.forEach((node) => {
          if (!RECOGNISED_BLOCK_TYPES.has(node.attr(BLOCK_TYPE_ATTRIBUTE))) {
            return;
          }
          // NOTE: The schema pads an empty div with a non-breaking space; drop it so the block is a
          // true empty shell whose only visible content is the CSS chrome.
          node.empty();
          node.attr('contenteditable', 'false');
          node.attr(
            BLOCK_NOTE_ATTRIBUTE,
            this._buildBlockNote(node.attr(BLOCK_TYPE_ATTRIBUTE), editor)
          );
        });
      });

      editor.serializer.addAttributeFilter(BLOCK_TYPE_ATTRIBUTE, (nodes) => {
        nodes.forEach((node) => {
          if (!RECOGNISED_BLOCK_TYPES.has(node.attr(BLOCK_TYPE_ATTRIBUTE))) {
            return;
          }
          node.empty();
          // NOTE: Strip by exclusion, not a hardcoded list: the ADR-0003 contract is an empty div
          // carrying nothing but the discriminator, whatever the schema or source view added.
          Object.keys(node.attributes.map)
            .filter((name) => name !== BLOCK_TYPE_ATTRIBUTE)
            .forEach((name) => node.attr(name, null));
        });
      });
    });
  }

  private _keepBodyBlockNotesLive(editor: Editor): void {
    // NOTE: SwitchMode is load-bearing: TinyMCE suppresses NodeChange in readonly, so a form that
    // flips to read-only after init would strand a live-looking remove cross (ADR-0003).
    editor.on('init SetContent NodeChange SwitchMode', () =>
      this._refreshBlockChrome(editor)
    );
  }

  // NOTE: A block is non-editable, so without this cross the only way to remove one is Delete.
  private _addBodyBlockRemoveAction(editor: Editor): void {
    editor.on('click', (event) => {
      const trigger = editor.dom.getParent(
        event.target as Node,
        `[${BLOCK_REMOVE_ATTRIBUTE}]`
      );
      const block =
        trigger && editor.dom.getParent(trigger, `[${BLOCK_TYPE_ATTRIBUTE}]`);
      if (!block) {
        return;
      }

      // NOTE: Deleting through the selection rather than dom.remove() so TinyMCE owns the undo
      // level, the caret it leaves behind, and the change event the form control listens for.
      editor.selection.select(block);
      editor.execCommand('Delete');
    });
  }

  private _refreshBlockChrome(editor: Editor): void {
    if (editor.removed || !editor.dom) {
      return;
    }

    editor.dom.select(`[${BLOCK_TYPE_ATTRIBUTE}]`).forEach((block) => {
      if (!RECOGNISED_BLOCK_TYPES.has(block.getAttribute(BLOCK_TYPE_ATTRIBUTE))) {
        return;
      }

      const note = this._buildBlockNote(
        block.getAttribute(BLOCK_TYPE_ATTRIBUTE),
        editor
      );
      if (block.getAttribute(BLOCK_NOTE_ATTRIBUTE) !== note) {
        editor.dom.setAttrib(block, BLOCK_NOTE_ATTRIBUTE, note);
      }

      const cross = block.querySelector(`[${BLOCK_REMOVE_ATTRIBUTE}]`);

      // NOTE: Gate on the editor's mode, never on publication status — a published article opens
      // at /edit and is fully editable. Box and note stay; only the dead affordance goes (ADR-0003).
      if (editor.mode.isReadOnly()) {
        if (cross) {
          editor.dom.remove(cross);
        }
        return;
      }

      if (!cross) {
        // NOTE: data-mce-bogus="all" keeps the cross out of the caret model and out of anything
        // TinyMCE serialises, on top of the serializer filter that empties the block anyway.
        block.appendChild(
          editor.dom.create(
            'span',
            {
              // NOTE: 'true', not '': DOMUtils.setAttrib reads an empty value as "remove the
              // attribute".
              [BLOCK_REMOVE_ATTRIBUTE]: 'true',
              'data-mce-bogus': 'all',
              title: 'Remove this block',
            },
            '&times;'
          )
        );
      }
    });
  }

  private _buildBlockNote(blockType: string, editor: Editor): string {
    const label = BLOCK_LABELS[blockType] ?? 'Content block';
    if (blockType !== DataBlockType.PRIVATE_MARKETS_FUNDS) {
      return label;
    }

    const count = this._countLinkedPrivateMarketsFunds(editor);
    if (count === 0) {
      return `${label} — no funds linked yet, this will not render`;
    }
    return count === 1
      ? `${label} — 1 linked fund renders here`
      : `${label} — ${count} linked funds render here`;
  }

  // NOTE: The website builds the block's roster from the body's PM fund links deduped by fund id,
  // so a fund linked three times in three formats is one row. Count ids, not anchors.
  private _countLinkedPrivateMarketsFunds(editor: Editor): number {
    const body = editor.initialized ? editor.getBody() : null;
    if (!body) {
      return 0;
    }

    const fundIds = new Set<string>();
    body
      .querySelectorAll('[data-private-markets-fund-id]')
      .forEach((link) =>
        fundIds.add(link.getAttribute('data-private-markets-fund-id'))
      );
    return fundIds.size;
  }

  // NOTE: PreInit for the same handler-order reason as _addBodyBlockChromeFilters above.
  private _addCardNoteTempAttr(editor: Editor): void {
    editor.on('PreInit', () => {
      // NOTE: addTempAttr, not the serializer.addAttributeFilter the body blocks use — it also
      // excludes the attribute from every undo level, without which merely opening an article and
      // clicking out of the body would prompt about unsaved changes. Full argument in ADR-0004.
      editor.serializer.addTempAttr(CARD_NOTE_ATTRIBUTE);
    });
  }

  // NOTE: PreInit for the same handler-order reason as _addBodyBlockChromeFilters above.
  private _addMentionFlashTempAttr(editor: Editor): void {
    editor.on('PreInit', () => {
      // NOTE: addTempAttr, not a serializer filter, so the flash reaches no undo level either -
      // full argument in ADR-0004.
      editor.serializer.addTempAttr(MENTION_FLASH_ATTRIBUTE);
    });
  }

  // NOTE: Separate from _keepBodyBlockNotesLive: the event lists differ in both directions, and
  // sharing one would run the block chrome's DOM walk on every keystroke in all six editors.
  // Why each of these six events is required — and why SwitchMode is not — is in ADR-0004.
  private _keepCardNotesLive(editor: Editor): void {
    editor.on('init SetContent NodeChange input undo redo', () =>
      this._refreshCardNotes(editor)
    );
  }

  private _refreshCardNotes(editor: Editor): void {
    if (editor.removed || !editor.dom) {
      return;
    }

    const linksByHost = new Map<HTMLElement, HTMLElement[]>();
    editor.dom.select(CARD_LINK_SELECTOR).forEach((link) => {
      // NOTE: Mirrors the website's pmFundCardInsertAfterLink exactly — the anchor itself, never
      // the nearest block ancestor, when there is no paragraph. Why it matters is in ADR-0004.
      const host = (link.closest('p') as HTMLElement) ?? link;
      const hosted = linksByHost.get(host);
      if (hosted) {
        hosted.push(link);
      } else {
        linksByHost.set(host, [link]);
      }
    });

    // NOTE: Clear first, keyed off what carries the attribute, so a note cannot outlive its reason
    // — the link deleted, its format changed, or the paragraph split around it.
    editor.dom.select(`[${CARD_NOTE_ATTRIBUTE}]`).forEach((element) => {
      if (!linksByHost.has(element)) {
        editor.dom.setAttrib(element, CARD_NOTE_ATTRIBUTE, null);
      }
    });

    linksByHost.forEach((links, host) => {
      const note = this._buildCardNote(links);
      if (host.getAttribute(CARD_NOTE_ATTRIBUTE) !== note) {
        editor.dom.setAttrib(host, CARD_NOTE_ATTRIBUTE, note);
      }
    });
  }

  // NOTE: The name is the anchor's own text, not Related Data — a pasted or source-view body
  // carries links that are in no panel (ADR-0001), and it is load-bearing, not decoration.
  // NOTE: No escapeHtml() here, deliberately, unlike the _get*Markup builders below — this is read
  // back by `content: attr(...)`, never parsed as HTML, so escaping would show &amp; (ADR-0004).
  private _buildCardNote(links: HTMLElement[]): string {
    // NOTE: Pluralised on the number of links, not the number of names — two cards render whether
    // or not both anchors happen to have text.
    const label = links.length > 1 ? 'Cards' : 'Card';
    const names = links
      .map((link) => link.textContent.trim())
      .filter((name) => name.length > 0);

    return names.length > 0
      ? `${label} · ${names.join(', ')}`
      : `${label} · fund card renders here`;
  }

  // NOTE: The calling button's panel renders independently of this component's *ngIf, so it can be
  // clicked before this.editor is assigned in setup(). Guard as _refreshBlockChrome does.
  insertBlock(blockType: DataBlockType): InsertBlockResult {
    if (!this.editor || this.editor.removed || !this.editor.dom) {
      return 'unavailable';
    }

    // NOTE: A different axis from the fund-count rule that leaves the button enabled with no funds
    // linked yet — nothing inserts into an editor that has told us it is not editable.
    if (this.editor.mode.isReadOnly()) {
      return 'unavailable';
    }

    const existing = this.editor.dom.select(
      `[${BLOCK_TYPE_ATTRIBUTE}="${blockType}"]`
    )[0];

    if (existing) {
      this.editor.selection.select(existing);
      this.editor.selection.scrollIntoView(existing);
      return 'existing';
    }

    // NOTE: Collapse first so a block-level insert lands after highlighted prose as a sibling,
    // rather than insertContent() replacing the selection with it.
    this.editor.selection.collapse(false);
    this.editor.insertContent(
      `<div ${BLOCK_TYPE_ATTRIBUTE}="${escapeHtml(blockType)}"></div>`
    );
    this._refreshBlockChrome(this.editor);
    return 'inserted';
  }

  onAddInlineImg(imgToAdd: IImgMediaDto): void {
    const imgMarkup = this._getImgMarkup(imgToAdd);
    this.editor.insertContent(imgMarkup);
  }

  onAddInlineVideo(videoToAdd: IVideoMediaDto): void {
    const videoMarkup = this._getVideoMarkup(videoToAdd);
    this.editor.insertContent(videoMarkup);
  }

  insertContent(content: ILinkInsert) {
    const selection = this.editor.selection.getContent({ format: 'text' });
    if (selection && selection != '') {
      content.name = selection;
    }

    let link = '';
    switch (content.dataLinkType) {
      case DataLinkType.COMPANY:
        link = this._getCompanyMarkup(content);
        break;
      case DataLinkType.SHARE:
        link = this._getShareMarkup(content);
        break;
      case DataLinkType.INVESTMENT_TRUST:
        link = this._getInvestmentTrustMarkup(content);
        break;
      case DataLinkType.ETF:
        link = this._getEtfMarkup(content);
        break;
      case DataLinkType.FUND:
        link = this._getFundMarkup(content);
        break;
      case DataLinkType.PRIVATE_MARKETS_FUND:
        link = this._getPrivateMarketsFundMarkup(content);
        break;
      case DataLinkType.MANAGER:
        link = this._getManagerMarkup(content);
        break;
      case DataLinkType.RELATED_CONTENT:
        link = this._getRelatedContentMarkup(content);
        break;
      case DataLinkType.EXTERNAL_LINK:
        link = this._getExternalLinkMarkup(content);
        break;
    }

    this.editor.insertContent(link);
  }

  // NOTE: A locate-only seam returning a Range, deliberately not folded into the insert below:
  // PM-144 (click a suggestion -> highlight and scroll to the mention) asks the same question
  // with a different verb, and burying the lookup inside an insert would make it rewrite this.
  findPrivateMarketsFundMention(clause: string, name: string): Range | null {
    if (!this.editor || this.editor.removed || !this.editor.initialized) {
      return null;
    }
    return findMentionRange(this.editor.getBody(), clause, name);
  }

  // NOTE: The body is the only reliable record of whether a fund is linked. The panel's
  // smartLinkedKeys only knows about accepts made in the current session, so a fund linked last
  // week is invisible to it. Attribute comparison rather than an interpolated attribute
  // selector, so a fund id is never parsed as CSS - the same reason
  // _countLinkedPrivateMarketsFunds reads the attribute off each node.
  hasPrivateMarketsFundBodyLink(fundId: string): boolean {
    if (!this.editor || this.editor.removed || !this.editor.initialized) {
      return false;
    }
    return Array.from(
      this.editor.getBody().querySelectorAll('[data-private-markets-fund-id]')
    ).some(
      (link) => link.getAttribute('data-private-markets-fund-id') === fundId
    );
  }

  /**
   * Wraps the fund's name, at a mention the run itself cited, in a body link. Tries each
   * evidence clause in order and stops at the first that yields one - all are equally cited, so
   * none is safer than another (ADR-0006).
   */
  insertPrivateMarketsFundBodyLink(
    request: IPrivateMarketsFundBodyLinkRequest
  ): PrivateMarketsBodyLinkResult {
    // The same guards as insertBlock: the related-data panel that owns the calling button
    // renders independently of this component, so it can be driven before the editor exists.
    if (
      !this.editor ||
      this.editor.removed ||
      !this.editor.dom ||
      !this.editor.initialized ||
      this.editor.mode.isReadOnly()
    ) {
      return 'unavailable';
    }

    // Ahead of any DOM walk: one auto-link per fund per article, and never a second one where
    // the editor has already linked the fund by hand somewhere the run never cited (ADR-0006).
    if (this.hasPrivateMarketsFundBodyLink(request.id)) {
      return 'already-linked';
    }

    for (const clause of request.clauses) {
      const range = this.findPrivateMarketsFundMention(clause, request.name);
      if (range) {
        this._wrapInPrivateMarketsFundLink(range, request);
        return 'linked';
      }
    }
    return 'not-found';
  }

  private _wrapInPrivateMarketsFundLink(
    range: Range,
    request: IPrivateMarketsFundBodyLinkRequest
  ): void {
    // NOTE: transact(), never replaceContent(): the mutation has to land inside TinyMCE's undo
    // stack or the editor's next Ctrl+Z takes back their own typing instead of this link.
    // transact() also dispatches 'change', which is what syncs the bound form control -
    // tinymce-angular listens on 'change input undo redo'.
    this.editor.undoManager.transact(() => {
      const holder = this.editor.getDoc().createElement('div');
      // NOTE: The body's own text, not the fund's name. The prose is being wrapped, not
      // rewritten, so its spacing and any inline markup splitting it have to survive - the match
      // is whitespace-normalised and crosses an <em>.
      // Built by the builder the manual "+" uses, so the two persist identical markup; the live
      // DOM differs only by the data-mce-href insertContent() picks up from editor.parser, which
      // the serializer strips.
      holder.innerHTML = this._getPrivateMarketsFundMarkup({
        id: request.id,
        name: range.toString(),
        dataLinkType: DataLinkType.PRIVATE_MARKETS_FUND,
        format: request.format,
      });
      // NOTE: deleteContents/insertNode rather than surroundContents, which throws when the span
      // only partially selects an element - the case where the name is split by an <em>.
      range.deleteContents();
      range.insertNode(holder.firstElementChild);
    });
    // The block note counts linked funds, and one has just arrived. Outside the transact, as
    // insertBlock also refreshes it outside the mutation it made.
    this._refreshBlockChrome(this.editor);
  }

  /**
   * Tints the blocks holding a suggestion's evidence and scrolls the first into view, returning
   * how many were marked — zero when the body no longer contains any of the clauses. Nothing is
   * persisted and nothing enters the undo stack (ADR-0007); the caller owns clearing it.
   */
  flashMentions(clauses: string[]): number {
    // The same guards as insertPrivateMarketsFundBodyLink, minus the read-only one: a flash is
    // navigation rather than authoring (ADR-0007).
    if (
      !this.editor ||
      this.editor.removed ||
      !this.editor.dom ||
      !this.editor.initialized
    ) {
      return 0;
    }

    const blocks = findClauseBlocks(this.editor.getBody(), clauses);
    this.clearMentionFlash();
    if (blocks.length === 0) {
      return 0;
    }

    this._forceReflow();
    blocks.forEach((block) =>
      // NOTE: Outside undoManager.transact(), unlike the body link one ticket earlier: that one
      // has to be undoable and to dirty the form, and this one must do neither.
      this.editor.dom.setAttrib(block, MENTION_FLASH_ATTRIBUTE, 'true')
    );
    this._scrollToBlock(blocks[0]);
    return blocks.length;
  }

  /**
   * Says why flashMentions() marked nothing for these clauses — gone from the body, or abridged
   * by the run into pieces no one block holds together. Call it only after a miss.
   */
  explainMentionMiss(clauses: string[]): ClauseMissReason {
    if (!this.editor || this.editor.removed || !this.editor.initialized) {
      return 'absent';
    }
    return explainClauseMiss(this.editor.getBody(), clauses);
  }

  /**
   * Removes the flash from wherever it still is. Safe to call when nothing is flashed, and when
   * typing has already replaced the blocks that carried it (ADR-0007).
   */
  clearMentionFlash(): void {
    if (!this.editor || this.editor.removed || !this.editor.dom) {
      return;
    }
    // NOTE: Keyed off what currently carries the attribute, never off remembered elements -
    // that is what makes a destroyed block a no-op instead of a stale reference.
    this.editor.dom
      .select(`[${MENTION_FLASH_ATTRIBUTE}]`)
      .forEach((block) =>
        this.editor.dom.setAttrib(block, MENTION_FLASH_ATTRIBUTE, null)
      );
  }

  // NOTE: Losing and regaining the attribute inside one frame coalesces into no mutation and no
  // animation. Reading a layout property between them is what makes a repeat click flash again.
  private _forceReflow(): number {
    return this.editor.getBody().offsetHeight;
  }

  // NOTE: Never Element.scrollIntoView(): it walks every scrollable ancestor across the iframe
  // boundary, dragging the CMS page with it. Only the editor's own window moves one, not both.
  private _scrollToBlock(block: Element): void {
    const win = this.editor.getWin();
    const rect = block.getBoundingClientRect();
    const centred =
      rect.top + win.scrollY - (win.innerHeight - rect.height) / 2;
    // NOTE: Asked of the iframe's own window, the only one that answers for content inside it.
    const prefersReducedMotion =
      win.matchMedia &&
      win.matchMedia('(prefers-reduced-motion: reduce)').matches;
    win.scrollTo({
      top: Math.max(0, centred),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  private _getImgMarkup(img: IImgMediaDto): string {
    const altText = escapeHtml(img.name?.trim());
    const imageTag = `<img src="${escapeHtml(img.inlineUrl)}" data-image-id="${escapeHtml(img.id)}" alt="${altText}" />`;
    return `<figure>${imageTag}</figure>`;
  }

  private _getVideoMarkup(videoToAdd: IVideoMediaDto): string {
    const thumbnailUrl = videoToAdd.thumbnailUrl ?? this.EMPTY_THUMBNAIL_URL;
    return `<img src="${escapeHtml(thumbnailUrl)}" data-video-id="${escapeHtml(videoToAdd.guid)}" />`;
  }

  private _getShareMarkup(insert: ILinkInsert) {
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return `<a href="#" data-link-type="${insert.dataLinkType}" data-share-instrument-id="${id}">${name}</a>`;
  }

  private _getCompanyMarkup(insert: ILinkInsert) {
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return `<a href="#" data-link-type="${insert.dataLinkType}" data-company-id="${id}">${name}</a>`;
  }

  private _getInvestmentTrustMarkup(insert: ILinkInsert) {
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return `<a href="#" data-link-type="${insert.dataLinkType}" data-investment-trust-id="${id}">${name}</a>`;
  }

  private _getEtfMarkup(insert: ILinkInsert) {
    const isin = escapeHtml(insert.isin);
    const segment = escapeHtml(insert.code);
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return (
      `<a href="#" data-link-type="${insert.dataLinkType}" data-isin="${isin}"  ` +
      `data-segment="${segment}" data-etf-instrument-id="${id}">${name}</a>`
    );
  }

  private _getFundMarkup(insert: ILinkInsert) {
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return `<a href="#" data-link-type="${insert.dataLinkType}" data-fund-id="${id}">${name}</a>`;
  }

  private _getManagerMarkup(insert: ILinkInsert) {
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return `<a href="#" data-link-type="${insert.dataLinkType}" data-manager-id="${id}">${name}</a>`;
  }

  private _getPrivateMarketsFundMarkup(insert: ILinkInsert) {
    // format reaches here as a plain string: search-list's addToBodyFormats input is generic, so
    // the BodyLinkFormat on ILinkInsert is an unchecked cast. Anything unrecognised degrades to
    // the regular format rather than being emitted, which keeps the anchor one of the three
    // shapes ADR-0002 promises the website.
    const isKnownFormat = Object.values(BodyLinkFormat).includes(
      insert.format as BodyLinkFormat
    );
    const formatAttribute = isKnownFormat
      ? ` data-format="${insert.format}"`
      : '';
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return `<a href="#" data-link-type="${insert.dataLinkType}"${formatAttribute} data-private-markets-fund-id="${id}">${name}</a>`;
  }

  private _getRelatedContentMarkup(insert: ILinkInsert): string {
    const id = escapeHtml(insert.id);
    const code = escapeHtml(insert.code);
    const name = escapeHtml(insert.name);
    return `<a href="#" data-link-type="article" data-content-id="${id}" data-delivery-target-code="${code}">${name}</a>`;
  }

  private _getExternalLinkMarkup(insert: ILinkInsert): string {
    const url = escapeHtml(toSafeHref(insert.url));
    const id = escapeHtml(insert.id);
    const name = escapeHtml(insert.name);
    return `<a href="${url}" data-link-type="external_link" data-link-id="${id}">${name}</a>`;
  }

  onRemoveImg(imgToRemove: IImgMediaDto): void {
    const imgRegex = '<img[^>]*data-image-id="' + imgToRemove.id + '"[^>]*>';
    this.replaceContent(imgRegex);
  }

  onRemoveVideo(videoToRemove: IVideoMediaDto): void {
    const videoRegex =
      '<img[^>]*data-video-id="' + videoToRemove.guid + '"[^>]*>';
    this.replaceContent(videoRegex);
  }

  replaceContent(regexString: string, newValue = ''): void {
    const updatedContent = this.editor
      .getContent()
      .replace(new RegExp(regexString, 'gi'), newValue);
    this.sectionFormControl.patchValue(updatedContent);
  }
}
